customElements.define(
  'product-listing',
  class ProductListing extends HTMLElement {
    connectedCallback() {
      this.setNodeSelectors();
      this.setEventListeners();
      this.setViewDefaultState();
    }

    /**
     * Sets the node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        filterForm: this.querySelector('[js-product-listing="filterForm"]'),
        viewButtons: this.querySelectorAll('[js-product-listing="view"]'),
        filterFormSubmit: this.querySelector('[js-product-listing="filterFormSubmit"]'),
        loadMore: this.querySelector('[js-product-listing="loadMore"]'),
        resultsGrid: this.querySelector('[js-product-listing="resultsGrid"]'),
        pagination: this.querySelector('[js-product-listing="pagination"]'),
      };
    }

    /**
     * Sets the event listeners.
     */
    setEventListeners() {
      if (this.nodeSelectors.filterForm) {
        this.nodeSelectors.filterForm.addEventListener('submit', (event) => {
          const params = new URLSearchParams(event.detail);
          const query = [];

          params.forEach((value, key) => {
            /**
             * Filter out empty or default values.
             */
            if (
              value === '' ||
              (key === 'sort_by' && value === this.nodeSelectors.filterForm.dataset.defaultSortBy) ||
              key === 'page'
            ) {
              return;
            }

            query.push(`${key}=${value}`);
          });

          this.setSection(query.join('&'));
        });
      }

      [...this.nodeSelectors.viewButtons].forEach((element) => {
        element.addEventListener('click', (event) => {
          const itemsPerRow = Number(event.currentTarget.dataset.itemsPerRow);

          this.setActiveView(itemsPerRow);
          localStorage.setItem('items-per-row', itemsPerRow);
        });
      });

      window.addEventListener(
        'resize',
        debounce(() => {
          this.setViewDefaultState(true);
        })
      );

      if (this.nodeSelectors.filterFormSubmit && this.nodeSelectors.filterForm) {
        this.nodeSelectors.filterFormSubmit.addEventListener('click', (event) => {
          event.preventDefault();

          this.nodeSelectors.filterForm.submit();
        });
      }

      if (this.nodeSelectors.loadMore) {
        this.nodeSelectors.loadMore.addEventListener('click', this.handleLoadMore.bind(this));
      }
    }

    /**
     * Handles the load more event.
     */
    async handleLoadMore() {
      const nextPageURL = this.getNextPageURL();
      const nextPageFetchURL = this.getNextPageFetchURL();

      this.setLoadMoreLoadingState();

      try {
        /**
         * Renders the next page results into the current grid.
         */
        await this.renderResultsByURL(nextPageFetchURL);

        /**
         * If we're on the last page, remove the pagination container.
         */
        if (
          this.nodeSelectors.pagination &&
          Number(nextPageURL.searchParams.get('page')) === Number(this.nodeSelectors.pagination.dataset.pages)
        ) {
          this.nodeSelectors.pagination.remove();
        }

        this.setLoadMoreLoadingState(true);
        this.pushURL(nextPageURL);
      } catch {
        this.setLoadMoreLoadingState(true);
      }
    }

    /**
     * Returns the URL for fetching the current section.
     * @returns {URL}
     */
    getNextPageFetchURL() {
      const url = new URL(this.getNextPageURL());

      if (this.hasAttribute('data-section-id')) {
        url.searchParams.append('section_id', this.dataset.sectionId);
      }

      return url;
    }

    /**
     * Constructs and returns the URL for the next page.
     * @returns {URL}
     */
    getNextPageURL() {
      const url = new URL(window.location.href);
      const currentPage = url.searchParams.has('page') ? Number(url.searchParams.get('page')) : 1;

      url.searchParams.set('page', currentPage + 1);

      return url;
    }

    /**
     * Sets the loading state for the load more button.
     * @param {Boolean} remove - Removes the loading state.
     */
    setLoadMoreLoadingState(remove = false) {
      if (!this.nodeSelectors.loadMore) {
        return;
      }

      if (remove) {
        this.nodeSelectors.loadMore.classList.remove('is-loading');
        return;
      }

      this.nodeSelectors.loadMore.classList.add('is-loading');
    }

    /**
     * Fetches and renders results from the given URL.
     * @param {URL} URL - The URL of the results to render.
     */
    async renderResultsByURL(URL) {
      const section = await fetch(URL).then((response) => response.text());
      const html = new DOMParser().parseFromString(section, 'text/html');
      const resultsGrid = html.querySelector('[js-product-listing="resultsGrid"]');

      /**
       * Applies a `dynamic` class to the banners that are about to be added to the grid.
       * - This allows the banner's to offset their row position.
       */
      resultsGrid.querySelectorAll('.product-listing-banner').forEach((banner) => {
        banner.classList.add('product-listing-banner--dynamic');
      });

      this.nodeSelectors.resultsGrid.insertAdjacentHTML('beforeend', resultsGrid.innerHTML);

      setAllDynamicImages(this.nodeSelectors.resultsGrid);
    }

    /**
     * Pushes the given URL to the address bar.
     * @param {URL} URL - The URL to push.
     */
    pushURL(URL) {
      window.history.replaceState({}, '', URL);
    }

    /**
     * Sets the default view state.
     * @param {Boolean} reset - Resets the state before setting defaults.
     */
    setViewDefaultState(reset = false) {
      let itemsPerRow = null;

      if (localStorage.getItem('items-per-row')) {
        itemsPerRow = Number(localStorage.getItem('items-per-row'));
      }

      if (reset) {
        this.style.removeProperty('--items-per-row');
      }

      if (window.matchMedia('(min-width: 768px)').matches) {
        if (itemsPerRow) {
          if (itemsPerRow === 1) {
            this.setActiveView(4);
            return;
          }

          this.setActiveView(itemsPerRow);
          return;
        }

        this.setActiveView(4);
        return;
      }

      if (itemsPerRow) {
        if (itemsPerRow === 4) {
          this.setActiveView(2);
          return;
        }

        this.setActiveView(itemsPerRow);
        return;
      }

      this.setActiveView(2);
    }

    /**
     * Sets the active items per row.
     * @param {Number} itemsPerRow - The number of items per row.
     */
    setActiveView(itemsPerRow) {
      this.style.setProperty('--items-per-row', itemsPerRow);
      this.dataset.itemsPerRow = itemsPerRow;

      [...this.nodeSelectors.viewButtons].forEach((element) => {
        if (Number(element.dataset.itemsPerRow) === itemsPerRow) {
          element.classList.add('is-active');
          return;
        }

        element.classList.remove('is-active');
      });
    }

    /**
     * Fetches and returns the section content.
     * @param {String} query - Optional query parameters.
     * @returns {Promise}
     */
    fetchSection(query) {
      return fetch(`${window.location.pathname}?section_id=${this.dataset.sectionId}${query ? `&${query}` : ''}`).then(
        (response) => response.text()
      );
    }

    /**
     * Sets the filter form loading state.
     * @param {Boolean} remove - Removes the loading state.
     */
    setFilterLoadingState(remove = false) {
      if (!this.nodeSelectors.filterFormSubmit) {
        return;
      }

      if (remove) {
        this.nodeSelectors.filterFormSubmit.classList.remove('is-loading');
        this.nodeSelectors.filterFormSubmit.disabled = false;
        return;
      }

      this.nodeSelectors.filterFormSubmit.classList.add('is-loading');
      this.nodeSelectors.filterFormSubmit.disabled = true;
    }

    /**
     * Sets the section.
     * - Fetches the section content, and injects it.
     * @param {String} query - Optional query parameters.
     */
    async setSection(query) {
      this.setFilterLoadingState();

      const section = await this.fetchSection(query);
      const html = new DOMParser().parseFromString(section, 'text/html');
      const main = html.querySelector('product-listing');

      theme.eventBus.emit('ToggleElement', {
        action: 'close',
        namespace: 'filter-drawer',
        transitionend: (() => {
          this.setFilterLoadingState(true);

          /**
           * Once the drawer has closed, update the product grid.
           */
          replaceElement(this, main, '[js-product-listing="container"]');

          this.setQueryParams(query);
          this.setNodeSelectors();
          this.setEventListeners();
          this.setViewDefaultState();

          setAllDynamicImages(this);
        }).bind(this),
      });
    }

    /**
     * Sets the collection query parameters.
     * @param {String} query - Optional query parameters.
     */
    setQueryParams(query) {
      window.history.pushState(null, null, query === '' ? window.location.pathname : `?${query}`);
    }
  }
);
